import axiosInstance from "@/api/axios-instance";

// The two event-type-keyed flag types managed from the event-type pages.
export type EventTypeFlagType =
  | "SESSION_USAGE_AUTOMATION"
  | "SESSION_AUTO_CANCELLATION";

export const EVENT_TYPE_FLAGS: {
  type: EventTypeFlagType;
  label: string;
  description: string;
}[] = [
  {
    type: "SESSION_USAGE_AUTOMATION",
    label: "Usage automation",
    description:
      "Automatically maps a booked session of this event type to a bundle usage.",
  },
  {
    type: "SESSION_AUTO_CANCELLATION",
    label: "Auto-cancellation",
    description: "Auto-cancels the session 15 minutes after it's scheduled.",
  },
];

export interface FeatureFlag {
  id: string;
  key: string;
  type: EventTypeFlagType;
  identifier: string;
  enabled: boolean;
  created_at: string;
}

// Flags for a single Calendly event-type URI (usually 0-2).
export async function getEventTypeFlags(
  identifier: string,
): Promise<FeatureFlag[]> {
  const r = await axiosInstance.get("/feature-flag", {
    params: { identifier },
  });
  return r.data?.flags ?? [];
}

// Flags for many event-type URIs at once (list view).
export async function getEventTypeFlagsBatch(
  identifiers: string[],
): Promise<FeatureFlag[]> {
  const list = identifiers.filter(Boolean);
  if (list.length === 0) return [];
  const r = await axiosInstance.get("/feature-flag", {
    params: { identifiers: list.join(",") },
  });
  return r.data?.flags ?? [];
}

// Create-or-toggle a flag by (type, identifier).
export async function setEventTypeFlag(data: {
  type: EventTypeFlagType;
  identifier: string;
  enabled: boolean;
  key?: string;
}): Promise<FeatureFlag> {
  return (await axiosInstance.post("/feature-flag", data)).data;
}

export interface ManagedEventFlagResult {
  type: EventTypeFlagType;
  enabled: boolean;
  total: number; // event types under the managed event
  updated: number; // those that actually carry a flag row now
  skipped: number; // event types with no calendly uri — cannot hold a flag
}

// Bulk-set one flag across every event type under a managed event. There is no
// managed-event-level flag row (FeatureFlag is keyed by Calendly event-type
// uri), so this fans out server-side in one transaction and the caller re-reads
// the children to show on / off / mixed.
export async function setManagedEventFlag(
  managedEventId: string,
  data: { type: EventTypeFlagType; enabled: boolean },
): Promise<ManagedEventFlagResult> {
  return (
    await axiosInstance.post(`/feature-flag/managed-event/${managedEventId}`, data)
  ).data;
}
